import React, { useState, useEffect } from 'react';
import { ProfileService, UserProfile } from '../services/profileService';
import { progressService } from '../services/progressService';
import { SponsorService, Sponsor, Sponsee, CreateSponsorRequest } from '../services/sponsorService';
import { UserProgramPreferencesService } from '../services/userProgramPreferencesService';
import { useDialog } from '../context/DialogContext';
import { parseDateSafe } from '../utils/dateUtils';
import InviteManagement from './InviteManagement';
import SponseeDashboard from './SponseeDashboard';
import ShareModal from './ShareModal';
import styles from '../styles/profileModal.module.css';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user }) => {
  const { showAlert, showConfirm } = useDialog();
  const [activeTab, setActiveTab] = useState<'profile' | 'sponsorship'>('profile');
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
  const [dateTrackingOptedOut, setDateTrackingOptedOut] = useState(false);
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);
  
  // Sponsor state
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [isLoadingSponsors, setIsLoadingSponsors] = useState(false);
  const [newSponsor, setNewSponsor] = useState<CreateSponsorRequest>({ program: '', name: '', email: '' });
  const [primaryProgram, setPrimaryProgram] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Sponsee state
  const [sponsees, setSponsees] = useState<Sponsee[]>([]);
  const [isLoadingSponsees, setIsLoadingSponsees] = useState(false);
  
  // Invitation and progress sharing
  const [inviteManagementOpen, setInviteManagementOpen] = useState(false);
  const [invitePreselectedType, setInvitePreselectedType] = useState<'sponsor' | 'sponsee' | 'friend' | undefined>();
  const [sponseeProgressOpen, setSponseeProgressOpen] = useState(false);
  const [selectedSponseeToken, setSelectedSponseeToken] = useState<string>('');
  const [selectedSponseeName, setSelectedSponseeName] = useState<string>('');
  
  // Share modal for viewing/creating sponsee snapshots
  const [shareModalForSponsee, setShareModalForSponsee] = useState<{userId: string; name: string; program: string} | null>(null);
  const [showAutoShareBanner, setShowAutoShareBanner] = useState(false);

  // Load profile data
  useEffect(() => {
    if (isOpen && user) {
      loadProfileData();
      loadSponsors();
      loadSponsees();
      loadPrimaryProgram();
    }
  }, [isOpen, user]);

  const loadProfileData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await ProfileService.getUserProfile();
      setProfileData(data);
      setEditForm({
        display_name: data.display_name,
        bio: data.bio,
        location: data.location,
        phone: data.phone
      });
      
      // Load date tracking preference
      setDateTrackingOptedOut(data.date_display_opted_out || false);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await ProfileService.updateUserProfile(editForm);
      if (profileData) {
        setProfileData({ ...profileData, ...editForm });
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    // Reset form to original values
    if (profileData) {
      setEditForm({
        display_name: profileData.display_name,
        bio: profileData.bio,
        location: profileData.location,
        phone: profileData.phone
      });
    }
  };

  // Sponsor management functions
  const loadSponsors = async () => {
    setIsLoadingSponsors(true);
    try {
      const sponsorList = await SponsorService.getSponsors();
      setSponsors(sponsorList);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    } finally {
      setIsLoadingSponsors(false);
    }
  };

  const loadSponsees = async () => {
    setIsLoadingSponsees(true);
    try {
      const sponseeList = await SponsorService.getSponsees();
      setSponsees(sponseeList);
    } catch (error) {
      console.error('Error loading sponsees:', error);
    } finally {
      setIsLoadingSponsees(false);
    }
  };

  const loadPrimaryProgram = async () => {
    try {
      const preferences = await UserProgramPreferencesService.getUserProgramPreferences();
      const primary = preferences?.primaryProgram || 'saa';
      setPrimaryProgram(primary);
      // Set default program for new sponsor
      setNewSponsor(prev => ({ ...prev, program: primary.toUpperCase() }));
    } catch (error) {
      console.error('Error loading primary program:', error);
      setPrimaryProgram('saa');
      setNewSponsor(prev => ({ ...prev, program: 'SAA' }));
    }
  };

  const handleAddSponsor = async () => {
    // Validate
    if (!newSponsor.program || !newSponsor.name.trim() || !newSponsor.email.trim()) {
      showAlert({
        message: 'Please fill in all fields (Program, Name, and Email).',
        variant: 'warning'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newSponsor.email)) {
      showAlert({
        message: 'Please enter a valid email address.',
        variant: 'warning'
      });
      return;
    }

    try {
      await SponsorService.createSponsor(newSponsor);
      await loadSponsors();
      // Reset form and hide it
      setNewSponsor({ program: primaryProgram.toUpperCase(), name: '', email: '' });
      setShowAddForm(false);
      showAlert({
        message: 'Sponsor added successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Error adding sponsor:', error);
      showAlert({
        message: 'Failed to add sponsor. Please try again.',
        variant: 'danger'
      });
    }
  };

  const handleDeleteSponsor = (sponsor: Sponsor) => {
    showConfirm({
      message: `Are you sure you want to remove ${sponsor.name} (${sponsor.email}) as your ${sponsor.program} sponsor?`,
      onConfirm: async () => {
        try {
          await SponsorService.deleteSponsor(sponsor.id);
          await loadSponsors();
          showAlert({
            message: 'Sponsor removed successfully.',
            variant: 'success'
          });
        } catch (error) {
          console.error('Error deleting sponsor:', error);
          showAlert({
            message: 'Failed to remove sponsor. Please try again.',
            variant: 'danger'
          });
        }
      },
      variant: 'danger'
    });
  };

  const handleDeleteSponsee = (sponsee: Sponsee) => {
    showConfirm({
      message: `Are you sure you want to remove ${sponsee.name} (${sponsee.email}) as your ${sponsee.program} sponsee?`,
      onConfirm: async () => {
        try {
          await SponsorService.deleteSponsee(sponsee.id);
          await loadSponsees();
          showAlert({
            message: 'Sponsee removed successfully.',
            variant: 'success'
          });
        } catch (error) {
          console.error('Error deleting sponsee:', error);
          showAlert({
            message: 'Failed to remove sponsee. Please try again.',
            variant: 'danger'
          });
        }
      },
      variant: 'danger'
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleToggleAutoShare = async (sponsorId: string, currentlyEnabled: boolean) => {
    try {
      const newState = !currentlyEnabled;
      await SponsorService.toggleAutoShare(sponsorId, newState);
      await loadSponsors();  // Reload to get updated auto-share status
      
      // Show banner on first enable
      if (newState && !currentlyEnabled) {
        setShowAutoShareBanner(true);
        setTimeout(() => setShowAutoShareBanner(false), 5000);  // Hide after 5 seconds
      }
      
      showAlert({
        message: newState 
          ? 'Auto-share enabled. Your sponsor can now view and create snapshots of your progress.' 
          : 'Auto-share disabled. Your sponsor can no longer view your progress.',
        variant: 'success'
      });
    } catch (error) {
      console.error('Error toggling auto-share:', error);
      showAlert({
        message: 'Failed to update auto-share settings. Please try again.',
        variant: 'danger'
      });
    }
  };

  const handleViewSponseeProgress = (sponsee: Sponsee) => {
    if (sponsee.user_id && sponsee.auto_share_enabled) {
      // Open Share modal for this sponsee
      setShareModalForSponsee({
        userId: sponsee.user_id,
        name: sponsee.name,
        program: sponsee.program
      });
    }
  };

  const handleOpenInviteManagement = (relationshipType: 'sponsor' | 'sponsee' | 'friend') => {
    setInvitePreselectedType(relationshipType);
    setInviteManagementOpen(true);
  };

  const handleInviteManagementClose = () => {
    setInviteManagementOpen(false);
    // Reload sponsors/sponsees after closing (in case invitations were sent and accepted)
    loadSponsors();
    loadSponsees();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Profile</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'profile' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'sponsorship' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('sponsorship')}
          >
            Sponsorship
          </button>
        </div>

        {/* Auto-share enabled banner */}
        {showAutoShareBanner && (
          <div className={styles.autoShareBanner}>
            <strong>✓ Auto-share enabled!</strong> Your sponsor can now generate snapshots of your progress.
          </div>
        )}

        <div className={styles.modalBody}>
          {activeTab === 'profile' && (
            <div className={styles.profileTab}>
              {isLoading ? (
                <div className={styles.loadingMessage}>Loading profile...</div>
              ) : error ? (
                <div className={styles.errorMessage}>{error}</div>
              ) : profileData ? (
                <div className={styles.profileContent}>
                  {isEditing ? (
                    <div className={styles.editForm}>
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Email</label>
                        <input
                          type="email"
                          value={user?.email || ''}
                          className={styles.fieldInput}
                          readOnly
                          disabled
                        />
                      </div>
                      
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Display Name</label>
                        <input
                          type="text"
                          value={editForm.display_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                          className={styles.fieldInput}
                        />
                      </div>
                      
                      <div className={styles.profileActions}>
                        <button
                          className={styles.saveButton}
                          onClick={handleSave}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          className={styles.cancelButton}
                          onClick={handleCancel}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Email</label>
                        <div className={styles.fieldValue}>{user?.email || 'Not available'}</div>
                      </div>
                      
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Display Name</label>
                        <div className={styles.fieldValue}>{profileData.display_name || 'Not provided'}</div>
                      </div>
                      
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Member Since</label>
                        <div className={styles.fieldValue}>
                          {profileData.member_since ? parseDateSafe(profileData.member_since).toLocaleDateString() : 'Not available'}
                        </div>
                      </div>
                      
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>Last Login</label>
                        <div className={styles.fieldValue}>
                          {profileData.last_login ? parseDateSafe(profileData.last_login).toLocaleDateString() : 'Not available'}
                        </div>
                      </div>
                      
                      <div className={styles.preferencesSection}>
                        <h3 className={styles.preferencesTitle}>Preferences</h3>
                        <div className={styles.preferenceItem}>
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={!dateTrackingOptedOut}
                              onChange={async (e) => {
                                setIsUpdatingPreferences(true);
                                try {
                                  await progressService.updateDateTrackingPreference(!e.target.checked);
                                  setDateTrackingOptedOut(!e.target.checked);
                                } catch (error) {
                                  console.error('Error updating date tracking preference:', error);
                                } finally {
                                  setIsUpdatingPreferences(false);
                                }
                              }}
                              disabled={isUpdatingPreferences}
                            />
                            <span className={styles.checkboxText}>
                              Show completion dates and timelines
                            </span>
                          </label>
                          <p className={styles.preferenceHelp}>
                            Track when you started and completed each step. You can disable this display while keeping your progress data.
                          </p>
                        </div>
                      </div>
                      
                      <div className={styles.profileActions}>
                        <button
                          className={styles.editButton}
                          onClick={handleEdit}
                          disabled={isLoading}
                        >
                          Edit Profile
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.noDataMessage}>No profile data available</div>
              )}
            </div>
          )}

          {activeTab === 'sponsorship' && (
            <div className={styles.sponsorshipTab}>
              {/* MY SPONSORS SECTION */}
              <div className={styles.sponsorshipSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h3 className={styles.sectionTitle}>My Sponsors</h3>
                    <p className={styles.sectionDescription}>
                      People who sponsor you in your recovery programs.
                    </p>
                  </div>
                  <button 
                    onClick={() => handleOpenInviteManagement('sponsor')}
                    className={styles.inviteButton}
                  >
                    + Invite Sponsor
                  </button>
                </div>

                {isLoadingSponsors ? (
                  <div className={styles.loading}>Loading sponsors...</div>
                ) : sponsors.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No sponsors added yet.</p>
                  </div>
                ) : (
                  <div className={styles.sponsorsList}>
                    {sponsors.map(sponsor => (
                      <div key={sponsor.id} className={styles.sponsorItem}>
                        <div className={styles.sponsorMainInfo}>
                          <div className={styles.sponsorHeader}>
                            <div className={styles.sponsorName}>{sponsor.name}</div>
                            <div className={styles.statusBadge} data-status={sponsor.confirmation_status}>
                              {sponsor.confirmation_status === 'confirmed' ? '✓ Confirmed' : 'Pending'}
                            </div>
                          </div>
                          <div className={styles.sponsorDetails}>
                            <span className={styles.sponsorProgram}>{sponsor.program}</span>
                            <span className={styles.sponsorEmail}>{sponsor.email}</span>
                          </div>
                          
                          {sponsor.confirmation_status === 'confirmed' && (
                            <div className={styles.autoShareControl}>
                              <label className={styles.toggleLabel}>
                                <input
                                  type="checkbox"
                                  checked={sponsor.auto_share_enabled || false}
                                  onChange={() => handleToggleAutoShare(sponsor.id, sponsor.auto_share_enabled || false)}
                                  className={styles.toggle}
                                />
                                <span>Auto-share my progress with this sponsor</span>
                                <span 
                                  className={styles.helpIcon}
                                  title="When enabled, your sponsor can view and create snapshots without you emailing them"
                                >
                                  ⓘ
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteSponsor(sponsor)}
                          className={styles.deleteButton}
                          title="Remove sponsor"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* MY SPONSEES SECTION */}
              <div className={styles.sponsorshipSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h3 className={styles.sectionTitle}>My Sponsees</h3>
                    <p className={styles.sectionDescription}>
                      People you sponsor in their recovery programs.
                    </p>
                  </div>
                  <button 
                    onClick={() => handleOpenInviteManagement('sponsee')}
                    className={styles.inviteButton}
                  >
                    + Invite Sponsee
                  </button>
                </div>

                {isLoadingSponsees ? (
                  <div className={styles.loading}>Loading sponsees...</div>
                ) : sponsees.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No sponsees added yet.</p>
                  </div>
                ) : (
                  <div className={styles.sponsorsList}>
                    {sponsees.map(sponsee => (
                      <div key={sponsee.id} className={styles.sponsorItem}>
                        <div className={styles.sponsorMainInfo}>
                          <div className={styles.sponsorHeader}>
                            <div className={styles.sponsorName}>{sponsee.name}</div>
                            <div className={styles.statusBadge} data-status={sponsee.confirmation_status}>
                              {sponsee.confirmation_status === 'confirmed' ? '✓ Confirmed' : 'Pending'}
                            </div>
                          </div>
                          <div className={styles.sponsorDetails}>
                            <span className={styles.sponsorProgram}>{sponsee.program}</span>
                            <span className={styles.sponsorEmail}>{sponsee.email}</span>
                          </div>
                          
                          {sponsee.confirmation_status === 'confirmed' && sponsee.auto_share_enabled && (
                            <button 
                              onClick={() => handleViewSponseeProgress(sponsee)}
                              className={styles.viewProgressButton}
                            >
                              View Progress
                            </button>
                          )}
                          
                          {sponsee.confirmation_status === 'confirmed' && !sponsee.auto_share_enabled && (
                            <div className={styles.noShareMessage}>
                              Sponsee hasn't enabled auto-share yet
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteSponsee(sponsee)}
                          className={styles.deleteButton}
                          title="Remove sponsee"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Management (send + history) */}
      <InviteManagement 
        isOpen={inviteManagementOpen}
        onClose={handleInviteManagementClose}
        preselectedRelationship={invitePreselectedType}
        preselectedProgram={primaryProgram.toUpperCase()}
      />

      {/* Sponsee Progress Dashboard */}
      <SponseeDashboard 
        isOpen={sponseeProgressOpen}
        onClose={() => setSponseeProgressOpen(false)}
        shareToken={selectedSponseeToken}
        sponseeName={selectedSponseeName}
      />

      {/* Share Modal for Sponsor viewing/creating sponsee snapshots */}
      {shareModalForSponsee && (
        <ShareModal
          user={user}
          isOpen={true}
          onClose={() => setShareModalForSponsee(null)}
          viewingForSponsee={shareModalForSponsee}
        />
      )}
    </div>
  );
};

export default ProfileModal; 